import { useEffect, useState } from "react";
import {
  Button,
  Datepicker,
  Label,
  Select,
  Modal,
  Textarea,
  TextInput,
} from "flowbite-react";
import { NOTIFICATION_TYPES } from "../../../../utils";
import {
  useAuth,
  useToast,
  useExpansionOrganizational,
} from "../../../../hooks";

export const EditExpansionOrganizational = ({
  accountId,
  expansion,
  isOpen,
  onClose,
  onExpansionUpdated,
}) => {
  const [contacts, setContacts] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    account_contact_id: "",
    additional_mrr: "",
    notes: "",
    activation_date: "",
  });
  const [loading, setLoading] = useState(false);

  const { apiClient } = useAuth();
  const showToast = useToast();
  const { updateOrganizationalExpansion } =
    useExpansionOrganizational(accountId);

  useEffect(() => {
    if (expansion) {
      setFormData({
        name: expansion.name || "",
        account_contact_id: expansion.account_contact?.id || "",
        additional_mrr: expansion.additional_mrr.amount || "",
        notes: expansion.notes || "",
        activation_date: expansion.activation_date || "",
      });
    }
  }, [expansion]);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await apiClient.get(
          `/accounts/${accountId}/contacts/`,
        );
        if (response.status === 200) {
          setContacts(response.data.results || response.data);
        }
      } catch (err) {
        console.error("Error fetching contacts:", err);
        showToast({
          content: "Error fetching contacts",
          type: NOTIFICATION_TYPES.error,
        });
      }
    };

    if (isOpen && accountId) {
      fetchContacts();
    }
  }, [apiClient, showToast, isOpen, accountId]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const expansionData = {
        name: formData.name,
        account_contact_id: formData.account_contact_id,
        additional_mrr: formData.additional_mrr,
        notes: formData.notes,
        activation_date: formData.activation_date,
      };

      await updateOrganizationalExpansion(expansion.id, expansionData);

      onClose();

      if (onExpansionUpdated) {
        onExpansionUpdated();
      }
    } catch (error) {
      console.error("Error updating expansion:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={isOpen} popup onClose={onClose}>
      <Modal.Header className="pl-5 pr-2 pt-3">
        <h3 className="text-xl font-medium text-gray-900 dark:text-white">
          Edit expansion opportunity
        </h3>
        <div className="text-xs font-normal leading-none text-gray-500 opacity-80">
          Edit existing expansion organisational opportunity
        </div>
      </Modal.Header>
      <Modal.Body className="mt-3 space-y-5 px-5">
        <form onSubmit={handleSubmit}>
          <div className="space-y-5">
            <TextInput
              id="name"
              placeholder="Give the expansion opp a name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              required
            />

            <div>
              <div className="mb-2 block">
                <Label htmlFor="account_contact_id">Account Contact</Label>
              </div>
              <Select
                id="account_contact_id"
                value={formData.account_contact_id}
                onChange={(e) =>
                  handleInputChange("account_contact_id", e.target.value)
                }
                required
              >
                <option value="">Select a contact...</option>
                {contacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.first_name} {contact.last_name} - {contact.email}
                  </option>
                ))}
              </Select>
            </div>

            <TextInput
              id="additional_mrr"
              type="number"
              step="0.01"
              placeholder="Potential additional €MRR this opportunity brings"
              value={formData.additional_mrr}
              onChange={(e) =>
                handleInputChange("additional_mrr", e.target.value)
              }
              required
            />

            <Textarea
              id="notes"
              rows={4}
              placeholder="Add a note to provide context around this opportunity"
              value={formData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
            />

            <Datepicker
              id="activation_date"
              className="w-[60%]"
              placeholder="Expected activation date"
              defaultDate={
                formData.activation_date
                  ? new Date(formData.activation_date)
                  : null
              }
              onSelectedDateChanged={(date) =>
                handleInputChange("activation_date", date?.toISOString())
              }
              required
            />

            <div className="flex w-full items-center justify-between">
              <Button type="button" color="gray" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" color="blue" disabled={loading}>
                {loading ? "Updating..." : "Update"}
              </Button>
            </div>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
